/**
 * SEC-007: Gestion sécurisée des tokens d'invitation
 * - Stockage avec expiration automatique
 * - Nettoyage des tokens expirés
 */

const INVITE_TOKEN_KEY = 'invite_token';
const INVITE_TOKEN_EXPIRY = 15 * 60 * 1000; // 15 minutes

interface StoredToken {
  token: string;
  expiry: number;
}

/**
 * Stocke un token d'invitation avec une expiration
 */
export function setInviteToken(token: string): void {
  const data: StoredToken = {
    token,
    expiry: Date.now() + INVITE_TOKEN_EXPIRY,
  };

  // Utiliser sessionStorage pour une meilleure sécurité
  // Note: sessionStorage persiste pendant la session de navigation,
  // ce qui est suffisant pour le flow OAuth qui reste sur le même domaine
  sessionStorage.setItem(INVITE_TOKEN_KEY, JSON.stringify(data));

  console.log('🔐 [INVITE_TOKEN] Token stocké (expire dans 15 min)');
}

/**
 * Récupère le token d'invitation s'il est valide
 * Retourne null si le token n'existe pas ou est expiré
 */
export function getInviteToken(): string | null {
  // Vérifier d'abord sessionStorage (nouveau système)
  const rawSession = sessionStorage.getItem(INVITE_TOKEN_KEY);

  if (rawSession) {
    try {
      const data: StoredToken = JSON.parse(rawSession);

      if (Date.now() > data.expiry) {
        console.log('⏰ [INVITE_TOKEN] Token expiré, nettoyage...');
        clearInviteToken();
        return null;
      }

      return data.token;
    } catch {
      // Format invalide, nettoyer
      clearInviteToken();
      return null;
    }
  }

  // Fallback: vérifier localStorage (ancien système)
  // Cela permet la migration depuis l'ancien code
  const rawLocal = localStorage.getItem(INVITE_TOKEN_KEY);

  if (rawLocal) {
    // Ancien format (juste le token en string)
    // Migrer vers le nouveau format
    console.log('🔄 [INVITE_TOKEN] Migration depuis localStorage...');
    setInviteToken(rawLocal);
    localStorage.removeItem(INVITE_TOKEN_KEY);
    return rawLocal;
  }

  return null;
}

/**
 * Supprime le token d'invitation de tous les storages
 */
export function clearInviteToken(): void {
  sessionStorage.removeItem(INVITE_TOKEN_KEY);
  localStorage.removeItem(INVITE_TOKEN_KEY); // Nettoyer aussi l'ancien storage
  console.log('🧹 [INVITE_TOKEN] Token nettoyé');
}

/**
 * Vérifie si un token d'invitation existe et est valide
 */
export function hasValidInviteToken(): boolean {
  return getInviteToken() !== null;
}
